import { Component, ErrorInfo, ReactNode } from 'react';
import './ModernUIErrorBoundary.scss';
import Button from 'components/common/Button/Button';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import { ReactComponent as DataSetLogo } from 'assets/img/logo-dataset-light.svg';
import log from 'common/ScalyrLogger';
import StackTrace from 'stacktrace-js';
import { buildUrl } from 'common/util/location';
import { UrlParams } from 'common/types';
import * as K from 'common/constants';
import { reloadPageWithoutSearchParams } from 'service/browser';

type Props = {
  fullHeight?: boolean;
};

type State = {
  error: Error | null;
  errorInfo: ErrorInfo | null;
  isDashboard: boolean;
};

/**
 * Only Class components can be a ErrorBoundary
 * @see https://reactjs.org/docs/error-boundaries.html
 */
class ModernUIErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { error: null, isDashboard: false } as State;
  }

  componentDidCatch(error: Error): void {
    // Check if this is a dashboard, as we can let them edit the JSON and possibly fix it
    const isDashboard = window.location.pathname.startsWith('/dash') && window.location.search.includes('page=');
    // Catch errors in any components below and re-render with error message
    this.setState({
      error,
      isDashboard,
    });

    /**
     * Here we leverage our source-maps to deliver meaningful errors to our logger.
     * Without using stacktrace-js errors in qa/staging/prod will represent minified source code and it will be very difficult to identify the root cause
     */
    StackTrace.fromError(error).then((stackframes) => {
      log.error('///// ErrorBoundary – Application crashed /////');
      log.error(error.message);
      log.error(stackframes[0].toString());
    });
  }

  refreshPage = (): void => {
    reloadPageWithoutSearchParams(window.location, [K.URL_PARAM.TEAM_TOKEN]);
  };

  editDashboardJSON = (): void => {
    const dashboardJSONFile = (new URLSearchParams(window.location.search)).get('page');
    window.location.href = buildUrl(K.ROUTE.CONFIG_FILE, {
      [K.URL_PARAM.PATH]: `/dashboards/${dashboardJSONFile}`,
      [K.URL_PARAM.ORIGINAL_URL]: window.location.href,
    } as UrlParams);
  };

  render(): ReactNode {
    const { error, isDashboard } = this.state;
    const { children, fullHeight } = this.props;
    if (error) {
      // Error path
      return (
        <div className={`ModernUIErrorBoundary ${fullHeight ? 'ModernUIErrorBoundary--FullHeight' : ''}`}>
          <div className="ModernUIErrorBoundary__Container">
            <DataSetLogo width={240} height={44} className="ModernUIErrorBoundary__Container__ScalyrLogo" />
            <h1 className="ModernUIErrorBoundary__Container__Title">Uh-oh, something went wrong</h1>
            <div className="ModernUIErrorBoundary__Actions">
              <h3 className="ModernUIErrorBoundary__Actions__Delimiter">or</h3>
              <Button onClick={this.refreshPage} leftIcon={<RefreshIcon />}>
                Refresh Page
              </Button>
              {isDashboard
                && (
                <>
                  <h3 className="ModernUIErrorBoundary__Actions__Delimiter">or</h3>
                  <Button onClick={this.editDashboardJSON} leftIcon={<EditIcon />}>
                    Edit Dashboard JSON
                  </Button>
                </>
                )}
            </div>
          </div>
        </div>
      );
    }

    // Normally, just render children
    return children;
  }
}

export default ModernUIErrorBoundary;
